import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getPageContent } from "../../utils";
import {
    selectEventsData,
    selectEventsStatus,
    selectEventsError,
    initializeAsync,
    getEventsAsync,
} from "./eventsSlice";

import { Button, Box, Paper, Menu, MenuItem, Tooltip, IconButton, Typography } from "@mui/material";

import { useAuth } from "../../hooks/useAuth";
import Grid from "@mui/material/Unstable_Grid2";
import { useNavigate } from "react-router-dom";
import FilterListIcon from "@mui/icons-material/FilterList";
import { config, styles } from "../../style";
import { EventCard } from "../../components/EventCard";
import { skeletonCard, skeletonCardUI, skeletonTable, skeletonTableUI } from "./skeleton";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import CardViewIcon from "../../icons/CardViewIcon";
import TableViewIcon from "../../icons/TableViewIcon";
import SortIcon from "@mui/icons-material/Sort";
import CustomiseTable from "../../components/CustomiseTable";
import PersonIcon from "@mui/icons-material/Person";
import { formatTimeWithZone } from "../../utils/date";
// Add custom imports here

const columnsData = [
    {
        fieldName: "eventName",
        name: "Event Name",
        hideCellValue: true,
        renderElement: (row, currentCellValue) => {
            return (
                <Typography component={"span"} color={"#101828"} fontSize={"14px"} lineHeight={"20px"} fontWeight={600}>
                    {currentCellValue}
                </Typography>
            );
        },
    },
    {
        fieldName: "speakerName",
        name: "Speaker Name",
        hideCellValue: true,
        renderElement: (row, currentCellValue) => {
            return (
                <>
                    {currentCellValue && (
                        <Box
                            sx={{
                                display: "inline-flex",
                                verticalAlign: "text-bottom",
                                boxSizing: "inherit",
                                textAlign: "left",
                                alignItems: "flex-start",
                                fontSize: "12px",
                            }}>
                            <PersonIcon fontSize="medium" sx={{ color: "gray", width: "20px" }} />
                            <Typography
                                variant="body"
                                fontSize={"14px"}
                                fontWeight={400}
                                color={"#101828"}
                                lineHeight={"20px"}
                                sx={{ marginLeft: "5px" }}>
                                {currentCellValue}
                            </Typography>
                        </Box>
                    )}
                </>
            );
        },
    },
    {
        fieldName: "startPlayDate",
        name: "Event Date",
        hideCellValue: true,
        renderElement: (row, currentCellValue) => {
            return (
                <>
                    <span style={{ marginLeft: "5px", textAlign: "start" }}>
                        {formatTimeWithZone(row?.startPlayDate)}
                        {row?.endPlayDate && row?.startPlayDate !== row?.endPlayDate && (
                            <> - {formatTimeWithZone(row?.endPlayDate)}</>
                        )}
                    </span>
                </>
            );
        },
    },
    {
        fieldName: "ownerName",
        name: "Owner",
        hideCellValue: true,
        renderElement: (row, currentCellValue) => {
            return (
                <>
                    {currentCellValue && (
                        <Box
                            sx={{
                                display: "inline-flex",
                                verticalAlign: "text-bottom",
                                boxSizing: "inherit",
                                textAlign: "left",
                                alignItems: "flex-start",
                                fontSize: "12px",
                            }}>
                            <PersonIcon fontSize="medium" sx={{ color: "gray", width: "20px" }} />
                            <Typography
                                variant="body"
                                fontSize={"14px"}
                                fontWeight={400}
                                color={"#101828"}
                                lineHeight={"20px"}
                                sx={{ marginLeft: "5px" }}>
                                {currentCellValue}
                            </Typography>
                        </Box>
                    )}
                </>
            );
        },
    },
];

const Events = () => {
    const data = useSelector(selectEventsData);
    const status = useSelector(selectEventsStatus);
    const error = useSelector(selectEventsError);
    const dispatch = useDispatch();
    const { getUserDetails } = useAuth();
    const navigate = useNavigate();
    // Add Variables

    const [anchorEl, setAnchorEl] = React.useState(null);
    const filterOpen = Boolean(anchorEl);
    const [view, setView] = useLocalStorage("eventView", "cards");
    const [anchorSortEl, setAnchorSortEl] = React.useState(null);
    const sortOpen = Boolean(anchorSortEl);
    const [eventSortBy, setEventSortBy] = useState("Newest");
    // const filter = data.type ? (data.type === "Open" ? "Active" : data.type === "Closed" ? "Archived" : data.type) : "Active";
    const [eventFilter, setEventFilter] = useState('Recent'); //changed  Open => Active

    const handleFilterMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleFilterMenuClose = (filterValue) => {
        if (!filterValue) {
            setAnchorEl(null);
            return;
        }

        // const filterValueText = filterValue === "Open" ? "Active" : filterValue === "Closed" ? "Archived" : filterValue;
        setEventFilter(filterValue);
        setAnchorEl(null);
        // dispatch(getEventsAsync({ getUserDetails, type: filterValue }));
    };

    useEffect(() => {
        if (status === "idle") {
            dispatch(initializeAsync({ getUserDetails, type: "Open" }));
        }
    }, [status, dispatch, getUserDetails]);

    // useEffect(() => {
    //     // data will load when switched to events router from other routes (menu item 'Active' selected by default)
    //     if (status !== "idle") {
    //         const filterValue = eventFilter === "Active" ? "Open" : eventFilter === "Archived" ? "Closed" : eventFilter;
    //         dispatch(getEventsAsync({ getUserDetails, type: filterValue }));
    //     }
    // }, []);

    const handleCardClick = (recordId) => {
        navigate("/event-detail/" + recordId);
    };

    const handleSortMenuClose = (sortBy) => {
        if (!sortBy) {
            setAnchorSortEl(null);
            return;
        }
        setEventSortBy(sortBy);
        setAnchorSortEl(null);
    };
    const handleSortMenuClick = (event) => {
        setAnchorSortEl(event.currentTarget);
    };

    const getRecords = () => {
        const type = eventFilter

        if (data?.records?.length > 0) {
            let recordsToSort = [
                ...data?.records.filter((record) => {
                    if (type === "Upcoming") {
                        return new Date(record.startPlayDate) >= new Date();
                    } else {
                        return new Date(record.startPlayDate) < new Date();
                    }
                }),
            ];

            if (eventSortBy === "Newest") {
                recordsToSort = recordsToSort.sort((a, b) =>
                    new Date(a.startPlayDate) > new Date(b.startPlayDate)
                        ? type === "Upcoming"
                            ? 1
                            : -1
                        : new Date(b.startPlayDate) > new Date(a.startPlayDate)
                        ? type === "Upcoming"
                            ? -1
                            : 1
                        : 0
                );
            } else if (eventSortBy === "Oldest") {
                recordsToSort = recordsToSort.sort((a, b) =>
                    new Date(a.startPlayDate) > new Date(b.startPlayDate)
                        ? type === "Upcoming"
                            ? -1
                            : 1
                        : new Date(b.startPlayDate) > new Date(a.startPlayDate)
                        ? type === "Upcoming"
                            ? 1
                            : -1
                        : 0
                );
            }
            return recordsToSort
        }
        return []
    };    

    let body = (
        <>
            {status === "initalLoading" ? (
                view === "cards" ? (
                    skeletonCard
                ) : (
                    skeletonTable
                )
            ) : (
                <>
                    <Box>
                        <Paper sx={{ p: "20px", display: "flex", flexDirection: "column" }} elevation={3}>
                            <Box style={{ fontSize: "12px", color: "gray" }}>Events</Box>
                            <Box style={{ textTransform: "uppercase", fontWeight: "800", fontSize: "21px" }}>{data?.userDetails?.contact?.accountName}</Box>
                        </Paper>
                    </Box>
                    <Box sx={{ marginTop: "1rem", mb: "20px" }}>
                        <Paper sx={{ p: "20px" }} elevation={3}>
                            <Grid container justifyContent="space-between">
                                <Grid item>
                                    <Box
                                        sx={{
                                            mb: "20px",
                                            display: "flex",
                                            flexDirection: "row",
                                            alignItems: "center",
                                        }}
                                    >
                                        <Box>
                                            <Button
                                                id="filter-events"
                                                aria-controls={filterOpen ? "basic-menu" : undefined}
                                                aria-haspopup="true"
                                                aria-expanded={filterOpen ? "true" : undefined}
                                                onClick={handleFilterMenuClick}
                                                startIcon={<FilterListIcon fontSize="small" sx={{ color: config.primaryColor }} />}
                                                sx={{ ...styles.outlinedButton }}
                                                variant="outlined"
                                            >
                                                {`Event Filter: ${eventFilter}`}
                                            </Button>
                                            <Menu
                                                id="basic-menu"
                                                anchorEl={anchorEl}
                                                open={filterOpen}
                                                onClose={() => handleFilterMenuClose("")}
                                                MenuListProps={{
                                                    "aria-labelledby": "filter-events",
                                                }}
                                            >
                                                <MenuItem onClick={() => handleFilterMenuClose("Recent")} value="Recent">
                                                    Recent Events
                                                </MenuItem>
                                                <MenuItem onClick={() => handleFilterMenuClose("Upcoming")} value="Upcoming">
                                                    Upcoming Events
                                                </MenuItem>
                                            </Menu>
                                        </Box>
                                    </Box>
                                </Grid>
                                <Grid item>
                                    <Box
                                        sx={{
                                            mb: "20px",
                                            display: "flex",
                                            flexDirection: "row",
                                            alignItems: "center",
                                        }}
                                    >
                                        <Box display="flex" justifyContent="flex-end" marginRight={"0.5rem"} overflow={"auto"} border={"1px solid #D0D5DD"} borderRadius={"10px"} padding={"2px"}>
                                            <Tooltip title="Card View">
                                                <IconButton
                                                    onClick={() => {
                                                        setView("cards");
                                                    }}
                                                    sx={{
                                                        width: 32,
                                                        height: 30,
                                                        backgroundColor: view === "cards" ? "#70707B" : "transparent",
                                                        color: view === "cards" ? "#fff" : "#000",
                                                        "&:hover": {
                                                            backgroundColor: view === "cards" ? "#70707B" : "transparent",
                                                        },
                                                        padding: 0,
                                                        borderRadius: "10px",
                                                    }}
                                                >
                                                    {/* <ViewModuleIcon fontSize="medium" /> */}
                                                    <CardViewIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Table View">
                                                <IconButton
                                                    onClick={() => setView("table")}
                                                    sx={{
                                                        width: 32,
                                                        height: 30,
                                                        backgroundColor: view === "table" ? "#70707B" : "transparent",
                                                        color: view === "table" ? "#fff" : "#000",

                                                        "&:hover": {
                                                            backgroundColor: view === "table" ? "#70707B" : "transparent",
                                                        },
                                                        padding: 0,
                                                        borderRadius: "10px",
                                                    }}
                                                >
                                                    <TableViewIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                        <Box>
                                            <Button
                                                id="sort-events"
                                                aria-controls={sortOpen ? "basic-menu" : undefined}
                                                aria-haspopup="true"
                                                aria-expanded={sortOpen ? "true" : undefined}
                                                onClick={handleSortMenuClick}
                                                startIcon={<SortIcon fontSize="small" sx={{ color: config.primaryColor }} />}
                                                sx={{ ...styles.outlinedButton }}
                                                variant="outlined"
                                            >
                                                {`Sort by: ${eventSortBy}`}
                                            </Button>
                                            <Menu
                                                id="basic-menu"
                                                anchorEl={anchorSortEl}
                                                open={sortOpen}
                                                onClose={() => handleSortMenuClose("")}
                                                MenuListProps={{
                                                    "aria-labelledby": "sort-events",
                                                }}
                                            >
                                                <MenuItem onClick={() => handleSortMenuClose("Newest")} value="Newest">
                                                    Newest
                                                </MenuItem>
                                                <MenuItem onClick={() => handleSortMenuClose("Oldest")} value="Oldest">
                                                    Oldest
                                                </MenuItem>
                                                {/* <MenuItem onClick={() => handleSortMenuClose("Event date")} value="Event date">
                                                    Event date
                                                </MenuItem> */}
                                            </Menu>
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>

                            {status === "loading" ? (
                                view === "cards" ? (
                                    skeletonCardUI
                                ) : (
                                    skeletonTableUI
                                )
                            ) : (
                                <>
                                    {view === "table" ? (
                                        <CustomiseTable
                                            columns={columnsData}
                                            rows={getRecords()}
                                            hoverEffect={false}
                                            componentStyleProps={{
                                                tableContainerSx: {
                                                    marginLeft: "-20px",
                                                    width: "calc(100% + 40px)",
                                                    boxShadow: "unset",
                                                    borderRadius: "unset",
                                                },
                                                tableHeadSx: {
                                                    "&.MuiTableHead-root .MuiTableCell-root": {
                                                        fontSize: "14px",
                                                        lineHeight: "18px",
                                                        fontWeight: 400,
                                                        color: "#000",
                                                        textTransform: "inherit",
                                                        background: "#FCFCFD",
                                                    },
                                                },
                                                tableHeaderCellSx: {
                                                    background: "#FCFCFD",

                                                    color: "red",

                                                    ".MuiTableHead-root &": {
                                                        borderTop: "1px solid #EAECF0",
                                                        borderBottom: "1px solid #EAECF0",
                                                    },
                                                    width: "200px",
                                                },
                                                tableBodySx: {
                                                    "&.MuiTableBody-root .MuiTableRow-root .MuiTableCell-root": {
                                                        borderBottom: "1px solid #EAECF0",
                                                    },
                                                    "&.MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root": {
                                                        borderBottom: "none",
                                                    },

                                                    "&.MuiTableBody-root .MuiTableCell-root": {
                                                        cursor: "pointer",
                                                    },
                                                },
                                            }}
                                            onRowClickHandler={(record) => {
                                                handleCardClick(record.id);
                                            }}
                                        />
                                    ) : (
                                        <Grid container spacing={3} alignItems="stretch">
                                            {getRecords()?.map((record) => (
                                                <Grid
                                                    item
                                                    key={record.id}
                                                    xs={12}
                                                    sm={6}
                                                    md={4}
                                                    sx={{
                                                        "@media (min-width: 1200px) and (max-width:1300px)": {
                                                            width: "50%",
                                                        },
                                                        "@media (min-width: 600px) and (max-width:650px)": {
                                                            width: "100%",
                                                        },
                                                    }}
                                                    onClick={() => handleCardClick(record.id)}
                                                >
                                                    <EventCard event={record} type="event"></EventCard>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    )}
                                </>
                            )}
                        </Paper>
                    </Box>

                    {/* <Box>
                <span style={{fontSize:'12px', color: 'gray'}}>
                    {data?.userDetails?.contact?.recordTypeName}
                </span>
            </Box>
            <Box>
                <span style={{textTransform: 'uppercase', fontWeight: '800'}}>
                    {data?.userDetails?.contact?.accountName}
                </span>
            </Box>
            <Box sx={{marginTop: '1rem'}}>
                <Grid container spacing={3}>
                    {data?.records?.map((record) => (
                        <Grid item key={record.id} lg={4} md={4} xs={12} onClick={() => handleCardClick(record.id)}>
                            <Card sx={{display: 'flex', flexDirection: 'column', cursor: 'pointer'}}>
                                <CardContent sx={{p:0,  paddingBottom: '0px !important'}}>
                                    <Box sx={{ p: 0 }}>
                                    <Grid sx={{p:0}} container wrap="nowrap" spacing={2}>
                                        <Grid item sx={{background: '#dfdfdf', paddingTop: '15px', paddingLeft: '15px'}}>
                                            <AccountBalanceIcon sx={{width: '4rem', height: '4rem'}} color="action"/>
                                        </Grid>
                                        <Grid item xs zeroMinWidth sx={{padding: '15px'}}>
                                                <Box>
                                                    <Typography noWrap>{record?.name}</Typography>
                                                </Box>
                                                <Box>
                                                    <span style={{fontSize: '12px'}}>{record?.stageName}</span>
                                                </Box>
                                        </Grid>
                                    </Grid>

                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box> */}
                </>
            )}
        </>
    );

    const modifiedStatus = status === "loading" ? "" : status;
    return getPageContent(body, modifiedStatus, error);
};
export { Events };
